<template>
  <div class="warehouse-page">
    <p v-if="error" class="banner error">{{ error }}</p>
    <p v-if="notice" class="banner ok">{{ notice }}</p>

    <div class="page-intro">
      <i class="fa-solid fa-circle-info"></i>
      <div>
        <strong>SAP A/R Invoices only</strong> — each row is one billed invoice line.
        <ul>
          <li><strong>Billed revenue</strong> = quantity × SAP <code>UnitPrice</code> (what was invoiced).</li>
          <li><strong>Invoice COGS</strong> = quantity × SAP <code>GrossBuyPrice</code> (cost on that invoice line).</li>
          <li><strong>Invoice GP</strong> = billed revenue minus invoice COGS (same idea as SAP line gross profit).</li>
        </ul>
      </div>
    </div>

    <div class="toolbar">
      <input v-model="filters.search" type="search" placeholder="Search SKU, item or invoice..." @keyup.enter="search">
      <button class="btn" type="button" @click="search" :disabled="exporting">
        <i class="fa-solid fa-magnifying-glass"></i> Search
      </button>
      <input v-model="filters.from" type="date" @change="search">
      <input v-model="filters.to" type="date" @change="search">
      <select v-model="filters.source" @change="search">
        <option value="all">All sources</option>
        <option v-for="source in lookups.sources" :key="source.code" :value="source.code">{{ source.name }}</option>
      </select>
      <select v-model="filters.location" @change="search">
        <option value="all">All locations</option>
        <option v-for="location in lookups.locations" :key="location" :value="location">{{ location }}</option>
      </select>
      <select v-model="filters.group" @change="search">
        <option value="all">All groups</option>
        <option v-for="group in lookups.groups || []" :key="group.key" :value="group.key">{{ group.label }}</option>
      </select>
      <button class="btn ghost" type="button" @click="reload" :disabled="exporting">Refresh</button>
      <button class="btn export" type="button" @click="exportCsv" :disabled="exporting || !total">
        <i class="fa-solid fa-file-export"></i> {{ exporting ? 'Exporting…' : 'Export CSV' }}
      </button>
    </div>

    <div class="table-card">
      <table>
        <thead>
          <tr>
            <th class="col-no">No</th>
            <th>Date</th>
            <th>SKU</th>
            <th>Item</th>
            <th>Location</th>
            <th>Group</th>
            <th class="col-num">Qty</th>
            <th class="col-money" title="Quantity × SAP UnitPrice on A/R invoice line">
              <span class="th-main">Billed revenue</span>
              <span class="th-sub">UnitPrice × Qty</span>
            </th>
            <th class="col-money" title="Quantity × SAP GrossBuyPrice on A/R invoice line">
              <span class="th-main">Invoice COGS</span>
              <span class="th-sub">GrossBuyPrice × Qty</span>
            </th>
            <th class="col-money" title="Billed revenue minus invoice COGS">
              <span class="th-main">Invoice GP</span>
              <span class="th-sub">Revenue − COGS</span>
            </th>
            <th title="SAP A/R invoice DocNum">
              <span class="th-main">Invoice no.</span>
              <span class="th-sub">SAP DocNum</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.id">
            <td class="col-no">{{ rowNumber(index) }}</td>
            <td>{{ row.saleDate }}</td>
            <td>{{ row.sku }}</td>
            <td>{{ row.itemName }}</td>
            <td>{{ row.location }}</td>
            <td>{{ row.locationGroupLabel || '—' }}</td>
            <td class="col-num">{{ number(row.quantity) }}</td>
            <td class="col-money">
              <span class="cell-main">{{ money(row.revenue) }}</span>
              <span class="cell-sub">{{ money(row.unitSellingPrice) }}/u</span>
            </td>
            <td class="col-money">
              <span class="cell-main">{{ money(row.cost) }}</span>
              <span class="cell-sub">{{ money(row.unitCostPrice) }}/u</span>
            </td>
            <td class="col-money">
              <span class="cell-main">{{ money(row.grossProfit) }}</span>
            </td>
            <td>{{ row.sourceRef || '—' }}</td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="11" class="empty">No invoice lines in this period. Pull A/R Invoices from SAP B1 on Data Flow.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pager">
      <label class="page-size">
        Rows
        <select v-model.number="pageSize">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </label>
      <span>{{ total }} invoice lines</span>
      <button type="button" :disabled="page <= 1" @click="page -= 1">Previous</button>
      <span>Page {{ page }}</span>
      <button type="button" :disabled="page * pageSize >= total" @click="page += 1">Next</button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import warehouseApi, { downloadWarehouseCsv } from '@/utils/warehouseApi'

function isoDate(date) {
  return date.toISOString().slice(0, 10)
}

const start = new Date()
start.setMonth(start.getMonth() - 6)

const error = ref('')
const notice = ref('')
const exporting = ref(false)
const page = ref(1)
const pageSize = ref(20)
const total = ref(0)
const rows = ref([])
const lookups = ref({ sources: [], locations: [], items: [], groups: [] })
const filters = reactive({
  search: '',
  source: 'all',
  location: 'all',
  group: 'all',
  from: isoDate(start),
  to: isoDate(new Date())
})

function money(value) {
  return `RM ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function number(value) {
  return Number(value || 0).toLocaleString()
}

function rowNumber(index) {
  return (page.value - 1) * pageSize.value + index + 1
}

async function reload() {
  try {
    error.value = ''
    notice.value = ''
    const { data } = await warehouseApi.get('/api/warehouse/sales', { params: { ...filters, page: page.value, pageSize: pageSize.value } })
    rows.value = data.rows || []
    total.value = data.total || 0
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load sales.'
  }
}

function search() {
  if (page.value !== 1) page.value = 1
  else reload()
}

async function exportCsv() {
  try {
    exporting.value = true
    error.value = ''
    await downloadWarehouseCsv('/api/warehouse/export/sales', { ...filters }, 'warehouse-sales.csv')
    notice.value = 'CSV export downloaded.'
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to export CSV.'
  } finally {
    exporting.value = false
  }
}

watch(page, reload)
watch(pageSize, () => {
  if (page.value !== 1) page.value = 1
  else reload()
})

onMounted(async () => {
  const { data } = await warehouseApi.get('/api/warehouse/lookups')
  lookups.value = data
  await reload()
})
</script>

<style scoped>
.page-intro {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 0.78rem;
  color: #1e3a8a;
  line-height: 1.45;
}

.page-intro i {
  margin-top: 2px;
  color: #2563eb;
}

.page-intro ul {
  margin: 6px 0 0;
  padding-left: 18px;
}

.page-intro code {
  font-size: 0.72rem;
  background: #dbeafe;
  padding: 1px 4px;
  border-radius: 4px;
}

.toolbar, .pager {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar input, .toolbar select, .btn, .pager button, .page-size select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 0.813rem;
}

.btn {
  background: #0ea5e9;
  color: white;
  border-color: #0ea5e9;
  cursor: pointer;
}

.btn.ghost {
  background: white;
  color: #475569;
}

.btn.export {
  background: #059669;
  border-color: #059669;
}

.btn i {
  margin-right: 4px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.banner {
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.813rem;
}

.banner.error {
  background: #fef2f2;
  color: #b91c1c;
}

.banner.ok {
  background: #ecfdf5;
  color: #047857;
}

.table-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 10px 8px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.78rem;
  text-align: left;
  vertical-align: top;
}

th {
  background: #f8fafc;
  color: #475569;
}

.th-main, .cell-main {
  display: block;
  font-weight: 600;
  color: #334155;
}

.th-sub, .cell-sub {
  display: block;
  font-size: 0.68rem;
  font-weight: 400;
  color: #64748b;
  margin-top: 2px;
}

.col-no {
  width: 44px;
  text-align: center;
  white-space: nowrap;
}

.col-num, .col-money {
  text-align: right;
  white-space: nowrap;
}

.empty {
  text-align: center;
  color: #64748b;
  padding: 24px;
}

.pager {
  justify-content: flex-end;
  color: #64748b;
  font-size: 0.813rem;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
}
</style>
